import * as React from 'react';
import { observer } from 'mobx-react';
import { classes, style } from 'typestyle';
import { ArtistLinkList } from './artist-link-list';
import { WaveformImage } from './waveform-image';
import { Duration } from './duration';
import {
  betweenJustified,
  center, centerCenter, content, endJustified, horizontal, horizontallySpaced, startJustified, vertical,
  verticallySpaced
} from 'csstips';
import { em, important, percent, px } from 'csx';
import { colorBackground, colorBrand, colorSubtle } from '../theme/color';
import { ButtonPill } from './button-pill';
import { SVGIcon } from './svg-icon';
import { TrackTile } from './track-tile';
import { resetAnchorStyles } from '../theme/reset';
import {ArtistSchema, LicenceSchema, TrackSchema} from '../types/schema';
import {formatCurrency} from '../lib/currency';
import {ImagePreloader} from '../types/fn';
import {AnalyticsController} from "../modules/analytics/controller";

export interface PageAccountLicencesModel {
  licences: Array<LicenceSchema>;
}

export interface PageAccountLicencesProps {
  model?: PageAccountLicencesModel;
  onClickLink(event: React.MouseEvent<any>): void
  onDownloadAudio(licenceId: string): void;
  onAttribution(licenceId: string): void;
  onViewOrder(licence: LicenceSchema): void;
  isMobile: boolean;
  preload: ImagePreloader;
  preloadWaveform: ImagePreloader;
  AddRemove: any;
  PlayButton: any;
  BrandSponsoredLabel: any;
  i18n?: {
    downloadAudio: string;
    downloadEULA: string;
    preparingEULA: string;
    attribution: string;
    viewOrder: string;
    empty: string;
  };
  analytics: AnalyticsController;
}

const constants = {
  tileSize: px(100)
}

@observer
export class PageAccountLicences extends React.Component<PageAccountLicencesProps, any> {
  render() {
    const { model, i18n = PageAccountLicences.i18n, onClickLink, isMobile, preload, preloadWaveform, AddRemove, PlayButton, BrandSponsoredLabel, onDownloadAudio, onViewOrder, onAttribution, analytics } = this.props;

    return (
      <div className={PageAccountLicences.styles.container}>
        {model.licences.length === 0 && (
          <div className={PageAccountLicences.styles.empty}>
            {i18n.empty}
          </div>
        )}
        {model.licences.map((licence, index) => !isMobile ? (
          <div className={PageAccountLicences.styles.trackDesktop} key={index.toString()}>
            <div className={PageAccountLicences.styles.trackDesktopSequence}>{licence.sequence}</div>
            <div className={PageAccountLicences.styles.trackDesktopTileContainer}>
              <TrackTile
                analytics={analytics}
                track={licence.track}
                preload={preload}
                noMeta={true}
                noLabels={true}
                PlayButton={PlayButton}
                className={PageAccountLicences.styles.tile}
                onClickLink={onClickLink}
              />
            </div>
            <div className={classes(PageAccountLicences.styles.trackDesktopMeta, PageAccountLicences.styles.meta)}>
              <a
                className={PageAccountLicences.styles.link}
                href={`/music/artists/${licence.track.artists[0].slug}/track/${licence.track.slug}`}
                onClick={onClickLink}
              >{licence.track.title}</a>
              <ArtistLinkList
                analytics={analytics}
                model={{ artists: licence.track.artists }}
                onClickLink={onClickLink}
              />
              {licence.brand_sponsored && (
                <div className={PageAccountLicences.styles.trackDesktopBrandSponsored}>
                  <BrandSponsoredLabel expanded={true}/>
                </div>
              )}
              <div className={classes(PageAccountLicences.styles.metaAction, PageAccountLicences.styles.metaActionDesktop)}>
                <ButtonPill onClick={onViewOrder.bind(null, licence)}>{i18n.viewOrder} <SVGIcon.ArrowMore size={12} /></ButtonPill>
                <div className={PageAccountLicences.styles.metaActionPrice}>
                  {formatCurrency(licence.amount, licence.order.locale.currency)}
                </div>
              </div>
            </div>
            <div className={PageAccountLicences.styles.trackDesktopWaveformContainer}>
              {licence.track.audio && licence.track.audio.identity && (
                <WaveformImage identity={licence.track.audio.identity} preloadWaveform={preloadWaveform} />
              )}
            </div>
            <div className={PageAccountLicences.styles.trackDesktopDurationContainer}>
              <Duration value={licence.track.duration} />
            </div>
            <div className={PageAccountLicences.styles.trackDesktopAddRemoveContainer}>
              <AddRemove track={licence.track} />
            </div>
            <div className={PageAccountLicences.styles.actions}>
              <ButtonPill muted={true} onClick={onAttribution.bind(null, licence.identity)}>{i18n.attribution}</ButtonPill>
              <ButtonPill disabled={!licence.eula_html_href} muted={true} href={licence.eula_html_href} target="_blank">{licence.eula_html_href ? i18n.downloadEULA : i18n.preparingEULA}</ButtonPill>
              <ButtonPill primary={true} onClick={onDownloadAudio.bind(null, licence.identity)}>{i18n.downloadAudio}</ButtonPill>
            </div>
          </div>
        ) : (
          <div className={PageAccountLicences.styles.trackMobile} key={index.toString()}>
            <div className={PageAccountLicences.styles.trackMobileHeader}>
              <div className={PageAccountLicences.styles.metaMobile}>
                <div>
                  <TrackTile
                    analytics={analytics}
                    track={licence.track}
                    preload={preload}
                    noMeta={true}
                    noLabels={true}
                    PlayButton={PlayButton}
                    AddRemove={AddRemove}
                    className={PageAccountLicences.styles.tile}
                    onClickLink={onClickLink}
                  />
                </div>
              </div>
              <div className={PageAccountLicences.styles.actions}>
                <ButtonPill muted={true} onClick={onAttribution.bind(null, licence.identity)}>{i18n.attribution}</ButtonPill>
                <ButtonPill disabled={!licence.eula_html_href} muted={true} href={licence.eula_html_href} target="_blank">{licence.eula_html_href ? i18n.downloadEULA : i18n.preparingEULA}</ButtonPill>
                <ButtonPill primary={true} onClick={onDownloadAudio.bind(null, licence.identity)}>{i18n.downloadAudio}</ButtonPill>
              </div>
            </div>
            <div className={PageAccountLicences.styles.trackMobileFooter}>
              <div className={PageAccountLicences.styles.trackMobileFooterSequence}>{licence.sequence}</div>
              <div className={classes(PageAccountLicences.styles.trackMobileFooterMeta, PageAccountLicences.styles.meta)}>
                <div>{licence.track.title}</div>
                <ArtistLinkList
                  analytics={analytics}
                  model={{ artists: licence.track.artists }}
                  onClickLink={onClickLink}
                />
                {licence.brand_sponsored && (
                  <div className={PageAccountLicences.styles.trackMobileBrandSponsored}>
                    <BrandSponsoredLabel expanded={true}/>
                  </div>
                )}
                <div className={classes(PageAccountLicences.styles.metaAction, PageAccountLicences.styles.metaActionMobile)}>
                  <div className={PageAccountLicences.styles.metaActionPrice}>
                    {formatCurrency(licence.amount, licence.order.locale.currency)}
                  </div>
                  <ButtonPill onClick={onViewOrder.bind(null, licence)}>{i18n.viewOrder} <SVGIcon.ArrowMore size={12} /></ButtonPill>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  static i18n = {
    downloadAudio: 'Download audio',
    downloadEULA: 'View license',
    preparingEULA: 'Preparing license...',
    attribution: 'View video attribution',
    viewOrder: 'View order',
    empty: 'Sorry you haven\'t got any licenses at the moment'
  };

  static styles = {
    container: style({
      position: 'relative',
      width: percent(100)
    }),
    link: style({
      color: colorBrand.toString(),
      $nest: { ...resetAnchorStyles(colorBrand.toString()) }
    }),
    empty: style({
      ...centerCenter,
      height: px(120),
      fontWeight: 200,
      fontSize: px(18),
      color: colorSubtle.toString()
    }),
    trackDesktop: style({
      ...horizontal,
      ...horizontallySpaced(20),
      ...center,
      padding: '10px 0',
    }),
    trackDesktopSequence: style({
      ...horizontal,
      ...startJustified,
      minWidth: px(32),
      fontSize: px(18),
      fontWeight: 'bold'
    }),
    trackDesktopTileContainer: style({
      ...horizontal,
      ...startJustified,
      width: constants.tileSize,
      height: constants.tileSize
    }),
    trackDesktopMeta: style({
      flex: 2,
      overflow: 'hidden'
    }),
    trackDesktopBrandSponsored: style({
      margin: '10px 0 0 0',
    }),
    trackDesktopWaveformContainer: style({
      ...centerCenter,
      position: 'relative',
      flex: 3,
      $nest: {
        '& img': {
          width: percent(100),
          height: px(30),
          borderRadius: px(50)
        }
      }
    }),
    trackDesktopDurationContainer: style({
      minWidth: px(70),
      ...horizontal,
      ...endJustified,
    }),
    trackDesktopAddRemoveContainer: style({
      ...horizontal,
      ...endJustified,
      minWidth: px(32),
    }),
    trackMobile: style({
      ...vertical,
      ...verticallySpaced(10),
      padding: '30px 0',
      borderBottom: `1px solid ${colorBackground.toString()}`,
      $nest: {
        '&:last-child': {
          borderBottom: 0
        }
      }
    }),
    trackMobileHeader: style({
      ...horizontal,
      ...horizontallySpaced(20),
      overflow: 'hidden',
      flex: 1
    }),
    trackMobileFooter: style({
      ...horizontal,
      ...horizontallySpaced(20),
      ...startJustified,
    }),
    trackMobileFooterSequence: style({
      minWidth: px(32),
      fontSize: px(18),
      fontWeight: 'bold'
    }),
    trackMobileBrandSponsored: style({
      textAlign: 'right',
      margin: '10px 0 0 0',
    }),
    trackMobileFooterMeta: style({
      flex: 1
    }),
    tile: style({
      width: constants.tileSize
    }),
    meta: style({
      lineHeight: em(1.1),
      fontSize: px(16),
      overflow: 'hidden',
      $nest: {
        '&> div': {
          overflow: 'hidden',
          whiteSpace: 'pre',
          textOverflow: 'ellipsis'
        },
        '&> div:nth-child(2)': {
          fontSize: px(12),
          color: colorSubtle.toString()
        },
        '& a': {
          fontSize: px(12),
          color: important(colorSubtle.toString())
        },
        '& a:hover': {
          color: important(colorBrand.toString())
        }
      }
    }),
    metaMobile: style({
      ...horizontal,
      ...horizontallySpaced(20),
      ...center,
      flex: 1,
      $nest: {
        '&> *:nth-child(1)': {
          ...startJustified,
          minWidth: px(32),
          fontSize: px(18),
          fontWeight: 'bold'
        },
        '&> *:nth-child(2)': {
          ...horizontal,
          ...startJustified,
          minWidth: px(32),
        },
        '&> *:nth-child(3)': {
          flex: 1,
          ...horizontal,
          ...endJustified,
        },
        '&> *:nth-child(4)': {
          ...horizontal,
          ...endJustified,
          minWidth: px(32),
        }
      }
    }),
    metaAction: style({
      margin: '10px 0 0 0',
      $nest: {
        'div': {
          fontSize: px(12)
        },
        'div svg': {
          marginLeft: px(10)
        },
        'a': {
          fontSize: px(12)
        },
        'a svg': {
          marginLeft: px(10)
        }
      }
    }),
    metaActionDesktop: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...content,
      ...center,
      ...startJustified,
      textAlign: 'left'
    }),
    metaActionMobile: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...content,
      ...center,
      ...endJustified,
      textAlign: 'right'
    }),
    metaActionPrice: style({
      fontSize: important('20px'),
    }),
    actions: style({
      ...vertical,
      ...betweenJustified,
      height: constants.tileSize,
      $nest: {
        'div': {
          fontSize: px(12)
        },
        'div svg': {
          marginLeft: px(10)
        },
        'a': {
          fontSize: px(12)
        },
        'a svg': {
          marginLeft: px(10)
        }
      }
    }),
    play: style({
      border: `1px solid ${colorBrand.toString()}`
    })
  }
}
